"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import facultyData from "./facultyData"; //

// FACULTY CARD COMPONENT
function FacultyCard({ teacher, index }) {
  const hasImage = teacher.image && teacher.image.length > 0;

  // Get initials for placeholder
  const initials = teacher.name
    ? teacher.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase()
    : "MSU";

  return (
    <div
      className="group relative overflow-hidden aspect-square cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 animate-fade-in-up bg-white"
      style={{
        animationDelay: `${index * 50}ms`,
        animationFillMode: "backwards",
      }}
    >
      {/* Background: Image or Placeholder */}
      <div className="relative w-full h-full overflow-hidden">
        {hasImage ? (
          <>
            <div className="relative w-full aspect-4/3 overflow-hidden">
              <Image
                src={teacher.image}
                alt={teacher.name}
                fill
                className="object-contain 
                transition-all duration-700
                group-hover:scale-105"
                sizes="(max-width:640px) 100vw,
                (max-width:1024px) 50vw,
                (max-width:1280px) 33vw"
              />
            </div>
            {/* Overlay for Image */}
            <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/5 transition-all duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent group-hover:from-black/95 group-hover:via-black/65 transition-all duration-500 ease-out" />
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200">
            <span className="text-4xl sm:text-5xl font-bold text-gray-300 select-none tracking-wider group-hover:text-blue-200 transition-colors duration-500">
              {initials}
            </span>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-500" />
          </div>
        )}
      </div>


      <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-400/30 transition-all duration-500" />


      <div className="absolute inset-x-0 bottom-0 p-6 text-white z-10">
        <div className="relative">

          <div className="transition-all duration-500 ease-out group-hover:opacity-0 group-hover:translate-y-2">
            <h3
              className={`text-xl sm:text-2xl font-bold leading-tight transition-colors duration-500 group-hover:text-blue-300 ${!hasImage
                ? "text-gray-800 group-hover:text-white"
                : "text-white"
                }`}
            >
              {teacher.name}
            </h3>
          </div>

          <div
            className="
              absolute inset-x-0 bottom-0
              opacity-0 translate-y-4
              group-hover:opacity-100 group-hover:translate-y-0
              transition-all duration-500 ease-out
            "
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-2 leading-tight text-blue-300">
              {teacher.name}
            </h3>

            <div className="pt-2 border-t border-white/20 group-hover:border-white/40 space-y-2 text-sm sm:text-base">
              {teacher.post && (
                <p className="font-light transform translate-y-2 group-hover:translate-y-0 transition-all duration-500 delay-50 text-gray-200">
                  {teacher.post}
                </p>
              )}

              {teacher.experience && (
                <p className="text-sm transform translate-y-2 group-hover:translate-y-0 transition-all duration-500 delay-150 text-gray-200">
                  <span className="font-semibold text-blue-300">
                    Experience:
                  </span>{" "}
                  {teacher.experience}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Shine Effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
        <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:left-full transition-all duration-1000 ease-out" />
      </div>
    </div>
  );
}

// MAIN TABS COMPONENT
export default function FacultyTabs() {
  // Use departments from your new data
  const departments = Array.isArray(facultyData?.departments)
    ? facultyData.departments
    : [];

  // Tabs based on department_name
  const departmentTabs = departments.map((dept) => ({
    id: dept.department_name,
    label: dept.department_name,
  }));

  const [activeTab, setActiveTab] = useState(departmentTabs[0]?.id || "");
  const tabsRef = useRef(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);

  useEffect(() => {
    if (departmentTabs.length > 0 && !activeTab) {
      setActiveTab(departmentTabs[0].id);
    }
  }, [departmentTabs, activeTab]);

  // SCROLL DETECTION
  useEffect(() => {
    const tabsContainer = tabsRef.current;
    if (!tabsContainer) return;

    const updateScrollButtons = () => {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainer;
      setShowPrev(scrollLeft > 0);
      setShowNext(scrollLeft + clientWidth < scrollWidth - 1);
    };

    // Run update after layout settles (rAF + short timeout) to account for images/fonts
    const runUpdate = () => {
      requestAnimationFrame(() => updateScrollButtons());
      setTimeout(updateScrollButtons, 100);
    };

    runUpdate();

    // Observe size changes (if supported)
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(runUpdate);
      ro.observe(tabsContainer);
    }

    const onScroll = runUpdate;
    tabsContainer.addEventListener("scroll", onScroll);
    window.addEventListener("resize", runUpdate);

    // Also listen for image loads inside container (Next/Image renders img under the hood)
    const imgs = tabsContainer.querySelectorAll("img");
    const onImgLoad = runUpdate;
    imgs.forEach((img) => img.addEventListener("load", onImgLoad));

    return () => {
      tabsContainer.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", runUpdate);
      if (ro) ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
    };
  }, [departmentTabs.length]);

  // SCROLL HANDLER
  const handleScroll = (direction) => {
    const tabsContainer = tabsRef.current;
    if (!tabsContainer) return;

    const scrollAmount = Math.max(tabsContainer.clientWidth * 0.5, 200);
    const newScrollLeft =
      direction === "left"
        ? tabsContainer.scrollLeft - scrollAmount
        : tabsContainer.scrollLeft + scrollAmount;

    tabsContainer.scrollTo({ left: newScrollLeft, behavior: "smooth" });

    // Ensure button visibility updates after smooth scroll finishes
    // (smooth scroll is async - use a short timeout or animation frame)
    setTimeout(() => {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainer;
      setShowPrev(scrollLeft > 0);
      setShowNext(scrollLeft + clientWidth < scrollWidth - 1);
    }, 400);
  };

  // Active department
  const activeDepartment = departments.find(
    (dept) => dept.department_name === activeTab,
  );

  if (!departments.length) {
    return (
      <div className="w-full bg-gray-50 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white shadow-lg p-8 max-w-2xl">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            No faculty data available
          </h2>
          <p className="text-sm text-gray-600">
            Check if <code>facultyData.departments</code> is defined correctly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 my-6 md:my-14 lg:my-20 page-container">
      <h2
        className="text-[20px] md:text-[25px] lg:text-[30px] xl:text-[42px]"
        style={{ fontFamily: "saans-bold" }}
      >
        Faculty
      </h2>
      <p className="text-black w-full text-[16px] md:text-[18px] mb-[20px]">
        Dedicated to shaping future professionals with expertise and hands-on
        learning
      </p>

      {/* TABS NAVIGATION */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm ">
        <div className="mx-auto pr-4">
          <div className="flex items-center justify-between gap-4">
            <div
              ref={tabsRef}
              className="flex overflow-x-auto scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {departmentTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    px-4 sm:px-6 py-4 text-xs sm:text-sm whitespace-nowrap
                    border-b-3 transition-all duration-300 flex-shrink-0 cursor-pointer
                    ${activeTab === tab.id
                      ? "text-[#0E74AA] border-b-[#0E74AA]"
                      : "text-black border-b-transparent hover:text-[#0E74AA]"
                    }
                  `}
                  style={{
                    borderBottomWidth: "3px",
                    fontFamily: "saans-medium",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Navigation Arrows */}
            {(showPrev || showNext) && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => handleScroll("left")}
                  disabled={!showPrev}
                  className={`
                    w-8 h-8 flex items-center justify-center 
                    border border-gray-300  hover:bg-gray-50 
                    transition-colors cursor-pointer
                    ${!showPrev
                      ? "opacity-30 cursor-not-allowed"
                      : "opacity-100"
                    }
                  `}
                  aria-label="Scroll tabs left"
                >
                  <span className="text-gray-600 text-lg">&lt;</span>
                </button>
                <button
                  onClick={() => handleScroll("right")}
                  disabled={!showNext}
                  className={`
                    w-8 h-8 flex items-center justify-center 
                    border border-gray-300  hover:bg-gray-50 
                    transition-colors cursor-pointer
                    ${!showNext
                      ? "opacity-30 cursor-not-allowed"
                      : "opacity-100"
                    }
                  `}
                  aria-label="Scroll tabs right"
                >
                  <span className="text-gray-600 text-lg">&gt;</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="mx-auto py-6">
        {activeDepartment ? (
          <>
            {/* Department Header */}
            <div className="mb-6">
              <h2
                className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 my-3"
                style={{ fontFamily: "saans-bold" }}
              >
                {activeDepartment.department_name}
              </h2>
            </div>

            {/* Faculty Grid */}
            {activeDepartment.faculty && activeDepartment.faculty.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {activeDepartment.faculty.map((teacher, index) => (
                  <FacultyCard
                    key={`${teacher.name}-${index}`}
                    teacher={teacher}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                No faculty found for this department
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-gray-500 py-12">
            <p className="mb-2">No faculty data available for selected tab</p>
            <p className="text-sm">Active Tab ID: {activeTab}</p>
          </div>
        )}
      </div>
    </div>
  );
}
